import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";
import CustomLegend from "./CustomLegend";

interface ChartData {
  xLabel: string;
  xLabel1: string;
  [key: string]: string | number;
}

interface LegendItem {
  key: string;
  label: string;
  color: string;
}

interface BarChartContainerProps {
  height?: number;
  data: ChartData[];
  legendConfig: LegendItem[];
  legendsPerRow?: number;
}

interface CustomTooltipProps extends TooltipProps<number, string> {
  labelMap: Record<string, string>;
}

/**
 * BarChartContainer component renders a responsive bar chart using Recharts
 * to visualize user data over different quarters and years.
 *
 * @component
 * @param {ChartData[]} data - The data array containing user counts per quarter and year.
 *
 * @example
 * <BarChartContainer data={sampleData} />
 */
const BarChartContainer: React.FC<BarChartContainerProps> = ({
  height = 350,
  data,
  legendConfig,
  legendsPerRow = 2,
}) => {
  // Get all keys that should be plotted
  const keys = legendConfig.map((item) => item.key);

  // Create lookup maps
  const keyToLabelMap = Object.fromEntries(
    legendConfig.map((item) => [item.key, item.label])
  );
  const keyToColorMap = Object.fromEntries(
    legendConfig.map((item) => [item.key, item.color])
  );

  const legendItems = legendConfig.map((item) => ({
    name: item.label,
    color: item.color,
  }));
  return (
    <>
      <CustomLegend items={legendItems} itemsPerRow={legendsPerRow} />
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          margin={{ top: 50, right: 10, left: -10, bottom: 20 }}
          barSize={30}
        >
          <CartesianGrid
            horizontal={true}
            vertical={false}
            strokeDasharray="3"
          />
          <XAxis
            dataKey="xLabel1"
            tickLine={false}
            tick={{ fontSize: 12, fill: "#626262" }}
            xAxisId="xLabel1Axis"
          />
          <XAxis
            dataKey="xLabel"
            axisLine={false}
            tickLine={false}
            tick={{ dy: 0, fontSize: 12, fill: "#626262" }}
            xAxisId="xLabelAxis"
            allowDuplicatedCategory={false}
          />
          <YAxis
            tickLine={false}
            tick={{ fontSize: 12, fill: "#626262" }}
            allowDecimals={false}
          />
          <Tooltip content={<CustomTooltip labelMap={keyToLabelMap} />} />
          {keys.map((key) => (
            <Bar
              key={key}
              dataKey={key}
              fill={keyToColorMap[key] ?? "#ccc"}
              xAxisId="xLabel1Axis"
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  labelMap,
}) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: "#fff",
          padding: "10px",
          border: "1px solid #ccc",
        }}
      >
        <p>{label}</p>
        {payload.map((entry, index) => (
          <p key={`item-${index}`} style={{ color: entry.color, margin: 0 }}>
            {labelMap[entry.dataKey as string] ?? entry.dataKey}: {entry.value}
          </p>
        ))}
      </div>
    );
  }

  return null;
};

export default BarChartContainer;
